import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api/client';
import type { MidiaDoProduto, Produto, TerritorioDoProduto } from '../lib/api/types';
import { useAuth } from '../lib/auth/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro, Vazio } from '../components/ui/Estados';
import { EditorDeTerritorio, territorioEmTexto } from '../components/territorio/EditorDeTerritorio';

/**
 * O produto: mídia e publicação (A7).
 *
 * <h3>Duas colunas porque são dois atos</h3>
 * À esquerda, a galeria — subir, ordenar, apagar. À direita, a publicação. Separá-los não é
 * arrumação de layout: subir a foto errada se conserta apagando, e publicar é o momento em
 * que a imagem passa a aparecer para qualquer pessoa que encoste o celular numa etiqueta
 * daquele produto. Um interruptor no meio do formulário de fotos seria acionado sem que
 * ninguém percebesse que acionou.
 *
 * <h3>A ordem tem consequência visível, e a tela diz qual</h3>
 * A primeira imagem é a que abre o passaporte. Uma lista arrastável sem essa frase é uma
 * lista que ninguém ordena — e o produto sai com a foto do verso na frente.
 *
 * <h3>O rascunho é conferível aqui, e em nenhum outro lugar</h3>
 * A rota pública recusa a imagem de produto não publicado. Esta tela a mostra por outra
 * rota, com token — é o que torna "conferir antes de soltar" possível.
 */
export function ProdutoPage() {
  const { productId = '' } = useParams();
  const { temPapel } = useAuth();
  const clienteDeConsulta = useQueryClient();

  const produtos = useQuery({
    queryKey: ['produtos'],
    queryFn: () => api.get<Produto[]>('/console/products'),
  });
  const galeria = useQuery({
    queryKey: ['produto', productId, 'midia'],
    queryFn: () => api.get<MidiaDoProduto[]>(`/console/products/${productId}/media`),
  });

  const produto = (produtos.data ?? []).find((p) => p.id === productId);
  const podeEditar = temPapel('COMPANY_ADMIN', 'COMPANY_CATALOG');
  const publicado = produto?.status === 'PUBLISHED';

  const recarregar = () => {
    void clienteDeConsulta.invalidateQueries({ queryKey: ['produto', productId, 'midia'] });
    void clienteDeConsulta.invalidateQueries({ queryKey: ['produtos'] });
  };

  const publicar = useMutation({
    mutationFn: (valor: boolean) =>
      api.put<Produto>(`/console/products/${productId}/publicacao`, { publicado: valor }),
    onSuccess: recarregar,
  });

  const ordenar = useMutation({
    mutationFn: (ids: string[]) =>
      api.put<MidiaDoProduto[]>(`/console/products/${productId}/media/ordem`, { ids }),
    onSuccess: recarregar,
  });

  const apagar = useMutation({
    mutationFn: (mediaId: string) =>
      api.remover<void>(`/console/products/${productId}/media/${mediaId}`),
    onSuccess: recarregar,
  });

  const fotos = galeria.data ?? [];

  const mover = (de: number, para: number) => {
    if (para < 0 || para >= fotos.length) {
      return;
    }
    // A lista vai inteira, e não o par trocado: o servidor exige que ela seja exatamente o
    // conjunto atual, e é isso que faz duas abas reordenando a mesma galeria discordarem em
    // vez de uma gravar por cima da outra em silêncio.
    const movida = fotos[de];
    if (!movida) {
      return;
    }
    const ids = fotos.map((f) => f.id);
    ids.splice(de, 1);
    ids.splice(para, 0, movida.id);
    ordenar.mutate(ids);
  };

  return (
    <>
      <PageHeader
        rastro={['Produtos', produto?.name ?? '—']}
        titulo={produto?.name ?? 'Produto'}
        subtitulo="As imagens que o consumidor vê ao ler a etiqueta — e o ato de colocá-las no ar."
        acoes={
          <span className={`chip ${publicado ? 'chip-teal' : 'chip-neutral'}`}>
            {publicado ? 'Publicado' : 'Rascunho'}
          </span>
        }
      />

      <div className="wrap">
        {produtos.isPending && <Carregando o="o produto" />}
        {produtos.isError && <Erro erro={produtos.error} />}
        {produtos.data && !produto && (
          <Vazio titulo="Produto não encontrado" detalhe="Ele pode ser de outra marca." />
        )}

        {produto && (
          <div className="duas">
            <div style={{ display: 'grid', gap: 16 }}>
              <div className="card cd">
                <div className="cd2-t">Imagens</div>

                {galeria.isPending && <Carregando o="as imagens" />}
                {galeria.isError && <Erro erro={galeria.error} />}
                {galeria.data && fotos.length === 0 && (
                  <Vazio
                    titulo="Nenhuma imagem"
                    detalhe="Sem foto, o passaporte abre só com o nome e a história do item."
                  />
                )}

                {fotos.length > 0 && (
                  <>
                    <div className="t-small" style={{ marginBottom: 12 }}>
                      A <strong>primeira</strong> é a que abre o passaporte. As outras aparecem
                      abaixo dela, na ordem desta lista.
                    </div>
                    <div className="galeria">
                      {fotos.map((f, i) => (
                        <figure key={f.id} className="foto">
                          <Miniatura productId={productId} midia={f} />
                          <figcaption>
                            {i === 0 && <span className="chip chip-teal">Principal</span>}
                            <div className="alt">{f.altText}</div>
                            <div className="meta">
                              {f.largura ?? '?'}×{f.altura ?? '?'} ·{' '}
                              {Math.round(f.bytes / 1024)} KB
                            </div>
                            {podeEditar && (
                              <div className="acoes" style={{ marginTop: 8 }}>
                                <button
                                  className="btn btn-ghost"
                                  disabled={i === 0 || ordenar.isPending}
                                  onClick={() => mover(i, i - 1)}
                                  title="Mover para a frente"
                                >
                                  ↑
                                </button>
                                <button
                                  className="btn btn-ghost"
                                  disabled={i === fotos.length - 1 || ordenar.isPending}
                                  onClick={() => mover(i, i + 1)}
                                  title="Mover para trás"
                                >
                                  ↓
                                </button>
                                <BotaoDeApagar
                                  ocupado={apagar.isPending}
                                  aoConfirmar={() => apagar.mutate(f.id)}
                                />
                              </div>
                            )}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  </>
                )}

                {(ordenar.isError || apagar.isError) && (
                  <div className="aviso ruim" style={{ marginTop: 12 }}>
                    {String(ordenar.error ?? apagar.error)}
                  </div>
                )}
              </div>

              {podeEditar && (
                <NovaImagem
                  productId={productId}
                  quantas={fotos.length}
                  aoSubir={recarregar}
                />
              )}
            </div>

            <div className="card cd">
              <div className="cd2-t">Publicação</div>

              {publicado ? (
                <>
                  <div className="aviso bom">
                    As imagens deste produto estão <strong>no ar</strong>. Quem ler a etiqueta
                    de qualquer item dele vê a galeria acima.
                  </div>
                  <p className="t-small">
                    Despublicar tira as imagens do ar sem apagar nenhuma. É o que se faz
                    quando a foto está errada e ainda não se sabe qual é a certa — a página
                    volta a abrir sem imagem, e nada se perde.
                  </p>
                </>
              ) : (
                <>
                  <div className="aviso">
                    Este produto está em <strong>rascunho</strong>. As imagens acima existem,
                    e <strong>ninguém fora daqui as alcança</strong> — nem por endereço
                    direto.
                  </div>
                  <p className="t-small">
                    Publicar não é irreversível, e não é como gerar serial: uma imagem no ar
                    sai do ar com um clique. Confira a ordem e o texto de cada foto antes.
                  </p>
                </>
              )}

              {publicar.isError && <div className="aviso ruim">{String(publicar.error)}</div>}

              {podeEditar ? (
                <button
                  className={`btn ${publicado ? 'btn-secondary' : 'btn-primary'}`}
                  disabled={publicar.isPending}
                  onClick={() => publicar.mutate(!publicado)}
                >
                  {publicado ? 'Despublicar' : 'Publicar'}
                </button>
              ) : (
                <div className="t-small">
                  Publicar exige o papel <strong>Catálogo</strong> ou{' '}
                  <strong>Administração</strong>.
                </div>
              )}
            </div>
          </div>
        )}

        {produto && <Territorio productId={productId} podeEditar={podeEditar} />}
      </div>
    </>
  );
}

/**
 * O território de distribuição (ADR-128) — onde o produto é vendido, e o que transforma o mapa em
 * alerta. Não declarado não alerta nada, e a tela diz isso em vez de parecer configurada.
 */
function Territorio({ productId, podeEditar }: { productId: string; podeEditar: boolean }) {
  const clienteDeConsulta = useQueryClient();
  const [editandoProduto, setEditandoProduto] = useState(false);
  const [editandoLote, setEditandoLote] = useState<string | null>(null);

  const territorio = useQuery({
    queryKey: ['produto', productId, 'territorio'],
    queryFn: () => api.get<TerritorioDoProduto>(`/console/products/${productId}/territory`),
  });

  const aoSalvar = (dados: TerritorioDoProduto) => {
    clienteDeConsulta.setQueryData(['produto', productId, 'territorio'], dados);
    setEditandoProduto(false);
    setEditandoLote(null);
  };

  const noProduto = useMutation({
    mutationFn: (codes: string[]) =>
      api.put<TerritorioDoProduto>(`/console/products/${productId}/territory`, { codes }),
    onSuccess: aoSalvar,
  });
  const noLote = useMutation({
    mutationFn: ({ batchId, codes }: { batchId: string; codes: string[] | null }) =>
      api.put<TerritorioDoProduto>(`/console/batches/${batchId}/territory`, { codes }),
    onSuccess: aoSalvar,
  });

  const t = territorio.data;

  return (
    <div className="card cd" style={{ marginTop: 16 }}>
      <div className="cd2-t">Território de distribuição</div>
      <div className="t-small" style={{ marginBottom: 12 }}>
        Onde este produto é vendido. Quando cinco ou mais leituras de consumidor aparecem na mesma UF
        ou país de fora, em sete dias, a segurança recebe um alerta de possível mercado paralelo ou
        carga desviada. A localização vem do IP: a UF costuma acertar, e uma leitura isolada não
        alerta.
      </div>

      {territorio.isPending && <Carregando o="o território" />}
      {territorio.isError && <Erro erro={territorio.error} />}

      {t && (
        <>
          {!editandoProduto ? (
            <div className="kv">
              <span className="k">Produto</span>
              <span className="v">
                {t.produto ? (
                  territorioEmTexto(t.produto)
                ) : (
                  <span className="chip chip-warn">não declarado — nenhum alerta</span>
                )}
                {podeEditar && (
                  <>
                    {' '}
                    <button className="btn btn-ghost" onClick={() => setEditandoProduto(true)}>
                      {t.produto ? 'Alterar' : 'Declarar'}
                    </button>
                  </>
                )}
              </span>
            </div>
          ) : (
            <EditorDeTerritorio
              inicial={t.produto}
              ocupado={noProduto.isPending}
              aoSalvar={(codes) => noProduto.mutate(codes)}
              aoCancelar={() => setEditandoProduto(false)}
            />
          )}
          {noProduto.isError && <Erro erro={noProduto.error} />}

          {t.lotes.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div className="t-cap" style={{ marginBottom: 6 }}>
                Lotes — um lote que vai para outro canal pode ter território próprio
              </div>
              {t.lotes.map((l) => (
                <div key={l.batchId}>
                  {editandoLote === l.batchId ? (
                    <div style={{ padding: '8px 0' }}>
                      <div className="t-small" style={{ fontWeight: 600, marginBottom: 6 }}>
                        Lote {l.code}
                      </div>
                      <EditorDeTerritorio
                        inicial={l.codes ?? t.produto}
                        ocupado={noLote.isPending}
                        aoSalvar={(codes) => noLote.mutate({ batchId: l.batchId, codes })}
                        aoCancelar={() => setEditandoLote(null)}
                        rotuloSalvar="Salvar território do lote"
                      />
                    </div>
                  ) : (
                    <div className="kv">
                      <span className="k mono-s">{l.code}</span>
                      <span className="v">
                        {l.codes ? territorioEmTexto(l.codes) : <span className="t-cap">usa o do produto</span>}
                        {podeEditar && (
                          <>
                            {' '}
                            <button className="btn btn-ghost" onClick={() => setEditandoLote(l.batchId)}>
                              {l.codes ? 'Alterar' : 'Território próprio'}
                            </button>
                            {l.codes && (
                              <button
                                className="btn btn-ghost"
                                disabled={noLote.isPending}
                                onClick={() => noLote.mutate({ batchId: l.batchId, codes: null })}
                              >
                                Usar o do produto
                              </button>
                            )}
                          </>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              ))}
              {noLote.isError && <Erro erro={noLote.error} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Apagar em dois toques.
 *
 * <p>Não é cerimônia: apagar tira os bytes do disco, e um botão vermelho encostado numa
 * setinha de reordenar é um botão que se aperta sem querer — a mão vai para a seta e erra
 * dois pixels. O primeiro toque só troca o rótulo; o segundo faz.
 *
 * <p>E ele volta sozinho em cinco segundos. Um botão que fica armado esperando é um botão
 * que a próxima pessoa encontra armado.
 */
function BotaoDeApagar({
  ocupado,
  aoConfirmar,
}: {
  ocupado: boolean;
  aoConfirmar: () => void;
}) {
  const [armado, setArmado] = useState(false);

  useEffect(() => {
    if (!armado) {
      return;
    }
    const t = setTimeout(() => setArmado(false), 5000);
    return () => clearTimeout(t);
  }, [armado]);

  if (!armado) {
    return (
      <button className="btn btn-ghost" disabled={ocupado} onClick={() => setArmado(true)}>
        Apagar
      </button>
    );
  }
  return (
    <button
      className="btn btn-danger"
      disabled={ocupado}
      onClick={() => {
        setArmado(false);
        aoConfirmar();
      }}
    >
      Apagar mesmo
    </button>
  );
}

/**
 * A miniatura de uma imagem que exige token.
 *
 * `<img src="/api/v1/...">` iria sem `Authorization` e voltaria 401 — a galeria mostraria
 * quadrados quebrados justamente na tela onde se confere a foto antes de publicá-la. Os
 * bytes vêm por `fetch` e viram um endereço local, revogado quando a imagem sai da tela.
 */
function Miniatura({ productId, midia }: { productId: string; midia: MidiaDoProduto }) {
  const [endereco, setEndereco] = useState<string | null>(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    let vivo = true;
    let atual: string | null = null;

    api
      .verImagem(`/console/products/${productId}/media/${midia.id}/bytes`)
      .then((url) => {
        if (!vivo) {
          // A tela saiu antes de os bytes chegarem: revoga aqui, ou o arquivo fica preso
          // na memória da aba até ela ser fechada.
          URL.revokeObjectURL(url);
          return;
        }
        atual = url;
        setEndereco(url);
      })
      .catch(() => vivo && setFalhou(true));

    return () => {
      vivo = false;
      if (atual) {
        URL.revokeObjectURL(atual);
      }
    };
  }, [productId, midia.id]);

  if (falhou) {
    return <div className="vazia">imagem indisponível</div>;
  }
  if (!endereco) {
    return <div className="vazia" />;
  }
  return (
    <img
      src={endereco}
      alt={midia.altText}
      width={midia.largura ?? undefined}
      height={midia.altura ?? undefined}
    />
  );
}

/**
 * O formulário de subida.
 *
 * <p>O texto alternativo é campo obrigatório <b>aqui</b>, e não uma validação que aparece
 * depois de o arquivo subir: a página do consumidor é pública, e uma foto sem alternativa
 * não existe para quem usa leitor de tela. O servidor recusa igual — os dois lados dizem a
 * mesma coisa, e é de propósito.
 */
function NovaImagem({
  productId,
  quantas,
  aoSubir,
}: {
  productId: string;
  quantas: number;
  aoSubir: () => void;
}) {
  const MAXIMO = 8;
  const campoDeArquivo = useRef<HTMLInputElement>(null);
  const [alt, setAlt] = useState('');

  const subir = useMutation({
    mutationFn: () => {
      const arquivo = campoDeArquivo.current?.files?.[0];
      if (!arquivo) {
        return Promise.reject(new Error('Escolha um arquivo.'));
      }
      const corpo = new FormData();
      corpo.append('file', arquivo);
      corpo.append('alt', alt);
      return api.enviarArquivo<MidiaDoProduto>(
        `/console/products/${productId}/media`,
        corpo,
      );
    },
    onSuccess: () => {
      setAlt('');
      if (campoDeArquivo.current) {
        campoDeArquivo.current.value = '';
      }
      aoSubir();
    },
  });

  const cheio = quantas >= MAXIMO;

  return (
    <div className="card cd">
      <div className="cd2-t">Nova imagem</div>
      {cheio ? (
        <div className="aviso">
          Este produto já tem {MAXIMO} imagens, que é o teto. Apague uma para subir outra —
          uma galeria maior é uma página que não carrega no 4G do corredor do supermercado.
        </div>
      ) : (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            subir.mutate();
          }}
        >
          <div className="campo">
            <label htmlFor="arq">Arquivo</label>
            {/* JPEG e PNG, e o servidor confere nos BYTES — o `accept` aqui é só cortesia
                para o seletor de arquivos, e não é o que protege nada. */}
            <input id="arq" type="file" accept="image/jpeg,image/png" ref={campoDeArquivo} />
            <span className="dica">JPEG ou PNG, até 5 MB.</span>
          </div>
          <div className="campo">
            <label htmlFor="alt">O que a imagem mostra</label>
            <input
              id="alt"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              maxLength={200}
              required
              placeholder="Frente da embalagem, com o selo visível"
            />
            <span className="dica">
              É o que a página oferece a quem não vê a foto. Descreva o que está nela, não o
              nome do arquivo.
            </span>
          </div>
          {subir.isError && <div className="aviso ruim">{String(subir.error)}</div>}
          <button className="btn btn-primary" type="submit" disabled={subir.isPending}>
            {subir.isPending ? 'Enviando…' : 'Subir imagem'}
          </button>
        </form>
      )}
    </div>
  );
}
